/**
 * /connect — Embedded token acquisition via postMessage.
 *
 * Opened as a popup (480×656) by developer apps. Validates the requesting
 * app's identity against the backend, then asks the authenticated user to
 * confirm sharing their active token with the app via postMessage.
 *
 * Query params:
 *   clientId  — the clientId of the requesting app
 *   origin    — the origin the app is running on (must be in allowedOrigins)
 *
 * Query params:
 *   reconnect — 'true' if the SDK already has a stored token for this user
 *               (set by connectViaPortal() when getStoredUserToken() is non-null)
 *   redirectUri — optional native app redirect target for deep-link flows
 *
 * Flow:
 *   1. Validate query params with Zod
 *   2. Fetch connect-config from public backend endpoint
 *   3. Validate origin against allowedOrigins (server is authoritative)
 *   4. Cross-check origin against document.referrer (browser-provided, defense-in-depth)
 *   5. Fetch authenticated user's token balance
 *   6. Show confirmation UI (reconnect variant if reconnect=true)
 *   7. On confirm: postMessage to explicit targetOrigin, then window.close()
 *   8. On cancel: window.close()
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Coins, AlertCircle, Loader } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getTokenBalance, getConnectToken } from '../services/token.service';
import { TokenBalance } from '../types';
import { appConfig } from '../config';

// ── Types ─────────────────────────────────────────────────────────────────

interface ConnectConfig {
  clientId: string;
  appName: string;
  allowedOrigins: string[];
}

interface ConnectGrant {
  token?: string;
  existingTokenPrefix?: string;
  isNewToken: boolean;
}

function getRedirectUri(searchParams: URLSearchParams): string | null {
  const redirectUri = searchParams.get('redirectUri');
  if (!redirectUri) return null;

  try {
    const parsed = new URL(redirectUri);
    if (!['home-cookbook:'].includes(parsed.protocol)) {
      return null;
    }

    return redirectUri;
  } catch {
    return null;
  }
}

interface TokenKitConnectMessage {
  type: 'TOKEN_KIT_TOKEN' | 'TOKEN_KIT_ERROR';
  token?: string;
  clientId?: string;
  isNewToken?: boolean;
  existingTokenPrefix?: string;
  errorCode?: string;
  errorMessage?: string;
}

type PageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; config: ConnectConfig; balance: TokenBalance; origin: string; grant: ConnectGrant };

function isPrivateOrLoopbackIPv4(hostname: string): boolean {
  const ipv4Match = hostname.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!ipv4Match) return false;

  const octets = ipv4Match.slice(1).map(Number);
  if (octets.some((value) => Number.isNaN(value) || value < 0 || value > 255)) {
    return false;
  }

  const [a, b] = octets;
  // Loopback + RFC1918 private ranges
  return a === 127 || a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
}

function isLocalDevOrigin(origin: string): boolean {
  try {
    const parsed = new URL(origin);
    const hostname = parsed.hostname.toLowerCase();

    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
      return true;
    }

    if (hostname.endsWith('.local')) {
      return true;
    }

    return isPrivateOrLoopbackIPv4(hostname);
  } catch {
    return false;
  }
}

// ── Param validation ──────────────────────────────────────────────────────

function validateParams(
  clientId: string,
  origin: string
): { valid: true; origin: string } | { valid: false; message: string } {
  if (!clientId || clientId.trim().length === 0) {
    return { valid: false, message: 'Missing required parameter: clientId.' };
  }
  if (!origin || origin.trim().length === 0) {
    return { valid: false, message: 'Missing required parameter: origin.' };
  }
  try {
    const parsed = new URL(origin);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return { valid: false, message: 'Invalid origin — must use http or https protocol.' };
    }
    return { valid: true, origin: parsed.origin };
  } catch {
    return { valid: false, message: 'Invalid origin parameter — must be a valid URL.' };
  }
}

// ── Component ─────────────────────────────────────────────────────────────

export default function ConnectPage() {
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [pageState, setPageState] = useState<PageState>({ status: 'loading' });
  const redirectUri = getRedirectUri(searchParams);

  const rawClientId = searchParams.get('clientId') ?? '';
  const rawOrigin = searchParams.get('origin') ?? '';
  const isReconnect = searchParams.get('reconnect') === 'true';

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    // Validate params
    const validation = validateParams(rawClientId, rawOrigin);
    if (!validation.valid) {
      setPageState({ status: 'error', message: validation.message });
      return;
    }

    const clientId = rawClientId.trim();
    const validatedOrigin = validation.origin;

    let cancelled = false;

    async function load() {
      try {
        // Fetch public connect config (no auth token needed)
        const configRes = await fetch(
          `${appConfig.apiUrl}/v1/apps/${encodeURIComponent(clientId)}/connect-config`
        );
        const configData = (await configRes.json()) as {
          success: boolean;
          data?: ConnectConfig;
        };

        if (cancelled) return;

        if (!configData.success || !configData.data) {
          setPageState({ status: 'error', message: 'The request to connect is from an unknown app.' });
          return;
        }

        const connectConfig = configData.data;

        // Enforce origin validation — server is the authoritative source of allowedOrigins
        if (!connectConfig.allowedOrigins.includes(validatedOrigin)) {
          setPageState({
            status: 'error',
            message: `This app is not authorized to receive tokens on origin: ${validatedOrigin}`,
          });
          return;
        }

        // Defense-in-depth: cross-check the claimed origin against document.referrer.
        // For local development origins (localhost/private LAN/.local), mobile browsers
        // often strip referrer during cross-context navigation, so skip strict checking.
        const isLocalDevelopmentRequest = isLocalDevOrigin(validatedOrigin);

        if (!document.referrer && !isLocalDevelopmentRequest) {
          setPageState({
            status: 'error',
            message: 'Unable to verify the origin of this connection request. Please open the connection directly from within the app.',
          });
          return;
        }

        if (document.referrer && !isLocalDevelopmentRequest) {
          try {
            const referrerOrigin = new URL(document.referrer).origin;
            if (referrerOrigin !== validatedOrigin) {
              setPageState({
                status: 'error',
                message: 'Origin mismatch — the requesting page does not match the declared origin.',
              });
              return;
            }
          } catch {
            setPageState({ status: 'error', message: 'Unable to verify connection origin.' });
            return;
          }
        }

        // Fetch the user's current token balance (requires Cognito session)
        const balanceRes = await getTokenBalance();
        if (cancelled) return;

        if (!balanceRes.success || !balanceRes.data) {
          setPageState({
            status: 'error',
            message: 'Unable to load token balance. Please try again.',
          });
          return;
        }

        // Preload the grant while the consent screen is visible so the final tap
        // can complete synchronously on mobile browsers.
        const grantRes = await getConnectToken(clientId);
        if (cancelled) return;

        if (!grantRes.success || !grantRes.data) {
          setPageState({
            status: 'error',
            message: grantRes.error || 'Unable to prepare the connection request. Please try again.',
          });
          return;
        }

        setPageState({
          status: 'ready',
          config: connectConfig,
          balance: balanceRes.data,
          origin: validatedOrigin,
          grant: grantRes.data,
        });
      } catch {
        if (!cancelled) {
          setPageState({
            status: 'error',
            message: 'Failed to load connection details. Please try again.',
          });
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [rawClientId, rawOrigin]);

  const handleAllow = useCallback(async () => {
    if (pageState.status !== 'ready') return;

    const { token, existingTokenPrefix } = pageState.grant;

    const sendToken = (payload: TokenKitConnectMessage) => {
      if (redirectUri) {
        const url = new URL(redirectUri);
        if (payload.type === 'TOKEN_KIT_TOKEN' && payload.token) {
          url.searchParams.set('token', payload.token);
          url.searchParams.set('clientId', payload.clientId ?? pageState.config.clientId);
          url.searchParams.set('isNewToken', String(payload.isNewToken ?? true));
        } else {
          url.searchParams.set('errorCode', payload.errorCode ?? 'TOKEN_KIT_ERROR');
          url.searchParams.set('errorMessage', payload.errorMessage ?? 'Unable to complete the connection request.');
          url.searchParams.set('clientId', payload.clientId ?? pageState.config.clientId);
        }

        window.location.href = url.toString();
        return;
      }

      window.opener?.postMessage(payload, pageState.origin);
      window.close();
    };

    // Fallback: no token in payload but existing token prefix is present
    if (!token && existingTokenPrefix) {
      const errorPayload: TokenKitConnectMessage = {
        type: 'TOKEN_KIT_ERROR',
        errorCode: 'TOKEN_ALREADY_EXISTS',
        errorMessage: 'You already have an active token. Please retrieve it from the dashboard or rotate it in settings.',
        existingTokenPrefix,
        clientId: pageState.config.clientId,
      };
      sendToken(errorPayload);
      return;
    }

    // If token is present, send it to the app.
    if (token) {
      const payload: TokenKitConnectMessage = {
        type: 'TOKEN_KIT_TOKEN',
        token,
        clientId: pageState.config.clientId,
        isNewToken: true,
      };
      sendToken(payload);
      return;
    }

    // Unexpected state
    alert('Unexpected response from server. Please try again.');
  }, [pageState, redirectUri]);

  const handleCancel = useCallback(() => {
    window.close();
  }, []);

  const handleSignOut = useCallback(async () => {
    await logout();
  }, [logout]);

  const isReady = pageState.status === 'ready';
  const isError = pageState.status === 'error';
  const isLoading = pageState.status === 'loading';

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
            <Coins size={28} className="auth-logo" />
            <span style={{ fontSize: '20px', fontWeight: '600' }}>AI Tokens</span>
          </div>
          {isReady && (
            <>
              <h1>{isReconnect ? 'Reconnect to' : 'Connect to'} {pageState.config.appName}</h1>
              <p>
                {isReconnect
                  ? `You've connected to ${pageState.config.appName} before. Confirm to share your current token.`
                  : `${pageState.config.appName} is requesting access to your AI Tokens balance.`}
              </p>
            </>
          )}
          {isError && (
            <>
              <h1>Connection Failed</h1>
              <p>Unable to complete the connection request.</p>
            </>
          )}
          {isLoading && (
            <>
              <h1>Connecting…</h1>
              <p>Loading connection details.</p>
            </>
          )}
        </div>

        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <Loader size={24} className="spin" style={{ color: 'var(--text-muted)' }} />
          </div>
        )}

        {isError && (
          <>
            <div className="alert alert-error">
              <AlertCircle size={16} />
              <span>{pageState.message}</span>
            </div>
            <button type="button" className="btn btn-ghost btn-block" onClick={handleCancel}>
              Close
            </button>
          </>
        )}

        {isReady && (
          <>
            <div className="connect-info">
              <div className="connect-info-row">
                <span className="connect-info-label">Signed in as</span>
                <span className="connect-info-value">{user?.email ?? '—'}</span>
              </div>
              <div className="connect-info-row">
                <span className="connect-info-label">Token</span>
                <span className="connect-info-value mono">
                  {pageState.balance.userToken.slice(0, 14)}••••
                </span>
              </div>
              <div className="connect-info-row">
                <span className="connect-info-label">Balance</span>
                <span className="connect-info-value">
                  {pageState.balance.available.toLocaleString()} tokens
                </span>
              </div>
              {isReconnect && (
                <div className="connect-info-row">
                  <span className="connect-info-label">Status</span>
                  <span className="connect-info-value" style={{ color: 'var(--accent)' }}>Previously connected</span>
                </div>
              )}
              <div className="connect-info-row">
                <span className="connect-info-label">Sharing with</span>
                <span className="connect-info-value">{pageState.origin}</span>
              </div>
            </div>

            <div className="connect-actions">
              <button type="button" className="btn btn-ghost btn-block connect-cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
              <button type="button" className="btn btn-accent btn-block" onClick={handleAllow}>
                {isReconnect ? 'Reconnect' : 'Allow Access'}
              </button>
            </div>

            {user && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '14px' }}>
                <button type="button" className="btn btn-ghost" onClick={() => void handleSignOut()}>
                  Sign Out
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
